<template>
  <b-container>
    <b-card class="mb-4">
      <b-row align-v="center">
        <b-col cols="12" md="6" lg="4">
          <label class="mb-2 font-weight-bold"
            >Select Date Range to View Reports</label
          >
          <DatePicker
            v-model="dateRange"
            range
            format="YYYY-MM-DD"
            placeholder="Select date range"
            @change="handleDateRangeChange"
            size="sm"
            style="width: 100%"
          />
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mt-3 mt-md-0">
          <b-button
            variant="primary"
            :disabled="!dateRange"
            @click="handleDateRangeChange"
            class="ml-md-2"
          >
            Refresh Reports
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div class="d-flex justify-content-center mb-4" v-if="isLoading">
      <b-spinner
        variant="success"
        label="Loading reports..."
        style="width: 3rem; height: 3rem"
      ></b-spinner>
      <p class="ml-3 mb-0 align-self-center">Loading reports...</p>
    </div>

    <b-alert show variant="info" v-if="!dateRange && !isLoading">
      Please select a date range above to view the reports.
    </b-alert>

    <b-row v-if="dateRange && !isLoading">
      <b-col md="12">
        <b-card>
          <div v-if="costAnalysisData && costAnalysisData.length">
            <ChartjsBarChart :cost="costAnalysisData" />
          </div>
          <div v-else class="text-center py-5">
            <i class="fas fa-chart-bar text-muted mb-3" style="font-size: 3rem;"></i>
            <h4 class="text-muted">No Cost Analysis Data Available</h4>
            <p>There is no cost analysis data for the selected date range. Try selecting a different period.</p>
          </div>
        </b-card>
      </b-col>
    </b-row>
    
    <b-row class="match-height" v-if="dateRange && !isLoading">
      <b-col>
        <b-card>
          <div v-if="productionStatusData && productionStatusData.length">
            <ProductionChart :statusData="productionStatusData" />
          </div>
          <div v-else class="text-center py-5">
            <i class="fas fa-chart-pie text-muted mb-3" style="font-size: 3rem;"></i>
            <h4 class="text-muted">No Production Status Data Available</h4>
            <p>There is no production status data for the selected date range. Try selecting a different period.</p>
          </div>
        </b-card>
      </b-col>
    </b-row>
    
    <b-row class="match-height" v-if="dateRange && !isLoading">
      <b-col>
        <b-card>
          <div v-if="productionLeadTimeData && productionLeadTimeData.length">
            <ChartjsHorizontalBarChart :leadTimeData="productionLeadTimeData" />
          </div>
          <div v-else class="text-center py-5">
            <i class="fas fa-hourglass-half text-muted mb-3" style="font-size: 3rem;"></i>
            <h4 class="text-muted">No Lead Time Data Available</h4>
            <p>There is no production lead time data for the selected date range. Try selecting a different period.</p>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import ChartjsBarChart from "./ChartjsBarChart.vue";
import ProductionChart from "./ProductionChart.vue";
import ChartjsHorizontalBarChart from "./ChartjsHorizontalBarChart";
import {
  BContainer,
  BRow,
  BCol,
  BSpinner,
  BCard,
  BButton,
  BAlert,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BSpinner,
    BCard,
    BButton,
    BAlert,
    ChartjsHorizontalBarChart,
    ProductionChart,
    ChartjsBarChart,
    DatePicker,
  },

  data() {
    const today = new Date();
    return {
      dateRange: [today, today],
      isInitialLoad: true,
      costAnalysisData: [],
      productionStatusData: [],
      productionLeadTimeData: [],
      isLocalLoading: false
    };
  },

  computed: {
    ...mapGetters("reportModule", {
      reportLoading: "loading",
    }),

    isLoading() {
      return this.reportLoading || this.isLocalLoading;
    }
  },

  methods: {
    ...mapActions("reportModule", [
      "getProductionCostAnalysisReportAction", 
      "getProductionReportAction",
      "getProductionLeadTimeAnalysisReportAction",
    ]),

    async handleDateRangeChange() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        return;
      }

      const dateParams = {
        dateFrom: this.formatDate(this.dateRange[0]),
        dateTo: this.formatDate(this.dateRange[1]),
      };

      try {
        this.isLocalLoading = true;
        await this.fetchAllReports(dateParams);
      } catch (error) {
        console.error("Error updating reports:", error);
      } finally {
        this.isLocalLoading = false;
      }
    },

    async fetchAllReports(dateParams) {
      console.log("dates", dateParams);
      try {
        const [costAnalysisResponse, productionStatusResponse, productionLeadTimeResponse] = await Promise.all([
          this.getProductionCostAnalysisReportAction(dateParams),
          this.getProductionReportAction(dateParams),
          this.getProductionLeadTimeAnalysisReportAction(dateParams)
        ]);
        
        this.costAnalysisData = costAnalysisResponse || [];
        this.productionStatusData = productionStatusResponse || [];
        this.productionLeadTimeData = productionLeadTimeResponse || [];

      } catch (error) {
        console.error("Error fetching production reports:", error);
        throw error;
      }
    },

    formatDate(date) {
      return date.toISOString().split("T")[0];
    },
  },

  async mounted() {
    try {
      this.isLocalLoading = true;
      await this.handleDateRangeChange();
    } catch (error) {
      console.error("Error during initial load:", error);
    } finally {
      this.isLocalLoading = false;
    }
  },
};
</script>